import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import SmartImage from '../components/SmartImage';
import { useAuth } from '../context/AuthContext';
import {
  Users,
  GraduationCap,
  Award,
  Shield,
  Heart,
  Landmark,
  Briefcase,
  Building2,
  ArrowRight,
  ChevronDown,
  Phone,
  Mail,
  MessageCircle,
  Download,
  FileText,
  CheckCircle,
  Loader2,
  Edit2,
  Quote,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  users: Users,
  school: GraduationCap,
  award: Award,
  shield: Shield,
  heart: Heart,
  landmark: Landmark,
  briefcase: Briefcase,
  building: Building2,
  check: CheckCircle,
};

const PartnershipsPage = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const { isAuthenticated, isAdmin } = useAuth();
  const isAdminUser = isAuthenticated && isAdmin && isAdmin();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/partnerships-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to load partnerships config:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${url}`;
    }
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const c = config || {};
  const hero = c.hero || {};
  const partnerTypes = c.partner_types || [];
  const logos = c.logos || [];
  const metrics = c.impact_metrics || [];
  const processSteps = c.process_steps || [];
  const caseStudies = c.case_studies || [];
  const downloads = c.downloads || {};
  const contact = c.contact || {};
  const faqs = c.faqs || [];

  // Group logos by category
  const logosByCategory = logos.reduce((acc, logo) => {
    const cat = logo.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(logo);
    return acc;
  }, {});

  const mouPdfUrl = getFileUrl(downloads.mou_pdf_url);
  const packPdfUrl = getFileUrl(downloads.schools_pack_pdf_url);

  return (
    <div className="min-h-screen bg-white" data-testid="partnerships-page">
      {/* Admin Edit Button */}
      {isAdminUser && (
        <Link
          to="/admin/partnerships"
          className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg shadow-lg hover:shadow-xl hover:border-primary transition-all text-sm font-medium text-slate-700 hover:text-primary"
          data-testid="edit-partnerships-btn"
        >
          <Edit2 className="w-4 h-4" />
          Edit Partnerships
        </Link>
      )}

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 bg-gradient-to-b from-primary/5 via-cyan-50/50 to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-primary mb-8 transition-colors"
          >
            <ChevronDown className="w-4 h-4 rotate-90" />
            Back to Home
          </Link>

          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
              {hero.badge || 'Partner With Us'}
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              {hero.title || 'Transform Lives Through Swimming'}
            </h1>
            <p className="text-xl text-slate-600 mb-10">
              {hero.subtitle || 'Join us in building a nation of confident swimmers.'}
            </p>

            {/* Dual CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to={hero.cta1_link || '/#contact'}>
                <Button size="lg" className="bg-primary hover:bg-primary/90 min-w-[200px]">
                  {hero.cta1_text || 'Book a Call'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              {packPdfUrl ? (
                <a
                  href={packPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-primary border-2 border-primary rounded-lg hover:bg-primary/5 transition-colors min-w-[200px] font-medium"
                >
                  <Download className="w-5 h-5" />
                  {hero.cta2_text || 'Download Pack'}
                </a>
              ) : (
                <Button variant="outline" size="lg" disabled className="min-w-[200px] opacity-60">
                  <Download className="w-5 h-5 mr-2" />
                  Pack Coming Soon
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Partner Types */}
      {partnerTypes.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-cyan-100 text-cyan-700 border-cyan-200">
                Who We Work With
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
                Partnership Opportunities
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {partnerTypes.map((type) => {
                const IconComponent = ICON_MAP[type.icon] || Building2;
                return (
                  <Card key={type.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">
                        {type.title}
                      </h3>
                      <p className="text-slate-600">{type.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Process Steps */}
      {processSteps.length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-emerald-200">
                How It Works
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
                Partnership Process
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.slice(0, 4).map((step, idx) => (
                <div key={step.id} className="relative">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 h-full">
                    <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg mb-4">
                      {idx + 1}
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-slate-600">{step.description}</p>
                  </div>
                  {idx < processSteps.length - 1 && idx < 3 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-slate-300" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Impact Metrics */}
      {metrics.length > 0 && (
        <section className="py-20 bg-primary text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                Our Impact Together
              </h2>
              <p className="text-primary-foreground/80 text-lg">
                Real results from our partnership programs
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {metrics.slice(0, 4).map((metric) => {
                const IconComponent = ICON_MAP[metric.icon] || Award;
                return (
                  <div key={metric.id} className="text-center">
                    <IconComponent className="w-10 h-10 mx-auto mb-4 opacity-80" />
                    <div className="text-4xl sm:text-5xl font-bold mb-2">
                      {metric.value}
                    </div>
                    <div className="text-primary-foreground/70">{metric.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Case Studies */}
      {caseStudies.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-amber-100 text-amber-700 border-amber-200">
                Success Stories
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
                Partner Case Studies
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {caseStudies.slice(0, 6).map((study) => (
                <Card key={study.id} className="border-0 shadow-lg overflow-hidden">
                  <CardContent className="p-0">
                    <div className="p-6 bg-gradient-to-r from-primary/5 to-cyan-50/50">
                      <Badge className="mb-2 bg-white text-primary">{study.partner_name}</Badge>
                      <h3 className="text-xl font-semibold text-slate-900">{study.title}</h3>
                    </div>
                    <div className="p-6 space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">Challenge</h4>
                        <p className="text-slate-700">{study.challenge}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">Solution</h4>
                        <p className="text-slate-700">{study.solution}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">Results</h4>
                        <p className="text-slate-700 font-medium">{study.results}</p>
                      </div>
                      {study.quote && (
                        <div className="pt-4 border-t border-slate-100">
                          <div className="flex items-start gap-3">
                            <Quote className="w-6 h-6 text-primary/30 flex-shrink-0 mt-1" />
                            <p className="text-slate-600 italic">{study.quote}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Partner Logos Wall */}
      {logos.length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-slate-200 text-slate-700 border-slate-300">
                Our Partners
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
                Trusted By Leading Organizations
              </h2>
            </div>

            {Object.keys(logosByCategory).length > 0 ? (
              <div className="space-y-10">
                {Object.entries(logosByCategory).map(([category, categoryLogos]) => (
                  <div key={category}>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider text-center mb-6">
                      {category}
                    </h3>
                    <div className="flex flex-wrap items-center justify-center gap-8">
                      {categoryLogos.map((logo, idx) => (
                        <div
                          key={logo.id || idx}
                          className="w-28 h-20 sm:w-36 sm:h-24 flex items-center justify-center bg-white rounded-lg shadow-sm p-4 grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100"
                        >
                          <SmartImage
                            src={getFileUrl(logo.url)}
                            alt={logo.alt || 'Partner logo'}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-center gap-8">
                {logos.map((logo, idx) => (
                  <div
                    key={logo.id || idx}
                    className="w-28 h-20 sm:w-36 sm:h-24 flex items-center justify-center bg-white rounded-lg shadow-sm p-4 grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100"
                  >
                    <SmartImage
                      src={getFileUrl(logo.url)}
                      alt={logo.alt || 'Partner logo'}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ))}
              </div>
            )}

            <p className="text-center text-xs text-slate-400 mt-8">
              Logos used with permission.
            </p>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-purple-100 text-purple-700 border-purple-200">
                FAQ
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
                Partnership Questions
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.slice(0, 6).map((faq, idx) => (
                <AccordionItem
                  key={faq.id || idx}
                  value={`faq-${idx}`}
                  className="bg-slate-50 rounded-xl border-0 px-6"
                >
                  <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Downloads Section */}
      {(mouPdfUrl || packPdfUrl) && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                Download Resources
              </h2>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {mouPdfUrl ? (
                <a
                  href={mouPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-6 py-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md hover:border-primary transition-all min-w-[250px]"
                >
                  <FileText className="w-8 h-8 text-primary" />
                  <div>
                    <div className="font-medium text-slate-900">MoU Template</div>
                    <div className="text-xs text-slate-500">Partnership agreement</div>
                  </div>
                  <Download className="w-5 h-5 text-slate-400 ml-auto" />
                </a>
              ) : (
                <div className="flex items-center gap-3 px-6 py-4 bg-slate-100 border border-slate-200 rounded-xl min-w-[250px] opacity-60 cursor-not-allowed">
                  <FileText className="w-8 h-8 text-slate-400" />
                  <div>
                    <div className="font-medium text-slate-500">MoU Template</div>
                    <div className="text-xs text-slate-400">Coming soon</div>
                  </div>
                </div>
              )}
              {packPdfUrl ? (
                <a
                  href={packPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-6 py-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md hover:border-primary transition-all min-w-[250px]"
                >
                  <FileText className="w-8 h-8 text-primary" />
                  <div>
                    <div className="font-medium text-slate-900">Schools Pack</div>
                    <div className="text-xs text-slate-500">Program details for schools</div>
                  </div>
                  <Download className="w-5 h-5 text-slate-400 ml-auto" />
                </a>
              ) : (
                <div className="flex items-center gap-3 px-6 py-4 bg-slate-100 border border-slate-200 rounded-xl min-w-[250px] opacity-60 cursor-not-allowed">
                  <FileText className="w-8 h-8 text-slate-400" />
                  <div>
                    <div className="font-medium text-slate-500">Schools Pack</div>
                    <div className="text-xs text-slate-400">Coming soon</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-primary to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
            Ready to Partner?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Let's discuss how we can work together to bring water safety education to your community.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <Link to={hero.cta1_link || '/#contact'}>
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90 min-w-[200px]">
                {hero.cta1_text || 'Book a Call'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            {contact.whatsapp_link && (
              <a
                href={contact.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors min-w-[200px] font-medium"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp Us
              </a>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-white/70">
            {contact.phone && (
              <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                {contact.phone}
              </a>
            )}
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                {contact.email}
              </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnershipsPage;
